import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getUserRoleKeys } from '../utils/routeAccess';
import {
  fetchUserPermissions,
  hasPermission,
  hasFactoryAccess,
  hasRole,
  hasGroup,
} from '../utils/permissions';

/**
 * Clave estable para React Query. Compartida entre todas las instancias del hook,
 * de modo que solo se hace UNA petición a /api/auth/permissions por sesión y el
 * resultado queda en caché.
 */
export const PERMISSIONS_QUERY_KEY = ['user-permissions'];

/**
 * Hook para permisos y roles del usuario (GET /api/auth/permissions).
 * Expone claves DEPT:Role para comprobaciones en render condicional.
 */
export const usePermissions = () => {
  const { data: permsData, isLoading, error } = useQuery({
    queryKey: PERMISSIONS_QUERY_KEY,
    queryFn: fetchUserPermissions,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const roleKeys = useMemo(() => getUserRoleKeys(permsData), [permsData]);

  const hasRoleKey = useCallback((key) => roleKeys.has(key), [roleKeys]);

  const hasAnyRoleKey = useCallback(
    (...keys) => keys.some((key) => roleKeys.has(key)),
    [roleKeys]
  );

  const hasAllRoleKeys = useCallback(
    (...keys) => keys.every((key) => roleKeys.has(key)),
    [roleKeys]
  );

  const checkPermission = (options) => {
    if (!permsData) return false;
    return hasPermission(permsData, options);
  };

  const checkFactoryAccess = (factory) => {
    if (!permsData) return false;
    return hasFactoryAccess(permsData, factory);
  };

  const checkRole = (role, factory = null, department = null) => {
    if (!permsData) return false;
    return hasRole(permsData, role, factory, department);
  };

  const checkGroup = (group) => {
    if (!permsData) return false;
    return hasGroup(permsData, group);
  };

  return {
    permissions: permsData ?? null,
    isLoading,
    error: error?.message ?? null,
    roleKeys,
    hasRoleKey,
    hasAnyRoleKey,
    hasAllRoleKeys,
    checkPermission,
    checkFactoryAccess,
    checkRole,
    checkGroup,
  };
};
